/**
 * Context Linking Utilities
 * Helpers for linking tasks to budgets, categories, transactions, lists, etc.
 */

import { db } from '@/config/firebase';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';

/**
 * Context types for linking tasks
 */
export const CONTEXT_TYPE = {
  BUDGET: 'budget',
  CATEGORY: 'category',
  TRANSACTION: 'transaction',
  LIST: 'list',
  GOAL: 'goal',
  NONE: 'none'
};

/**
 * Get friendly name for context type
 */
export function getContextTypeName(contextType) {
  switch (contextType) {
    case CONTEXT_TYPE.BUDGET:
      return 'budget envelope';
    case CONTEXT_TYPE.CATEGORY:
      return 'category';
    case CONTEXT_TYPE.TRANSACTION:
      return 'transaction';
    case CONTEXT_TYPE.LIST:
      return 'shopping list';
    case CONTEXT_TYPE.GOAL:
      return 'savings goal';
    default:
      return 'item';
  }
}

/**
 * Verify if a linked entity exists in the database
 * Returns { exists: boolean, message?: string }
 */
export async function verifyContextLinkExists(contextType, contextId, userId) {
  if (!contextType || contextType === CONTEXT_TYPE.NONE || !contextId || !userId) {
    return { exists: false, message: 'Invalid link - missing information' };
  }

  try {
    switch (contextType) {
      case CONTEXT_TYPE.TRANSACTION: {
        // Check in user's transactions collection
        const txRef = doc(db, `users/${userId}/transactions`, contextId);
        const txSnap = await getDoc(txRef);
        if (txSnap.exists()) {
          return { exists: true };
        }
        // Also check plannerTransactions
        const plannerRef = doc(db, `plannerTransactions/${userId}/transactions`, contextId);
        const plannerSnap = await getDoc(plannerRef);
        if (plannerSnap.exists()) {
          return { exists: true };
        }
        return { 
          exists: false, 
          message: 'This transaction no longer exists. It may have been deleted or was temporary context.'
        };
      }

      case CONTEXT_TYPE.GOAL: {
        const goalRef = doc(db, `users/${userId}/goals`, contextId);
        const goalSnap = await getDoc(goalRef);
        if (goalSnap.exists()) {
          return { exists: true };
        }
        return { 
          exists: false, 
          message: 'This savings goal no longer exists. It may have been completed or deleted.'
        };
      }

      case CONTEXT_TYPE.BUDGET: {
        // Budget/envelope - check in categoryEnvelopes
        const envelopeRef = doc(db, `users/${userId}/categoryEnvelopes`, contextId);
        const envelopeSnap = await getDoc(envelopeRef);
        if (envelopeSnap.exists()) {
          return { exists: true };
        }
        return { 
          exists: false, 
          message: 'This budget envelope no longer exists. It may have been deleted or renamed.'
        };
      }

      case CONTEXT_TYPE.CATEGORY: {
        // Categories are typically part of envelopes, so redirect to budget page
        // Just verify we have a valid category reference
        if (contextId) {
          return { exists: true }; // Categories are always navigable to the budget filter
        }
        return { exists: false, message: 'Invalid category reference.' };
      }

      case CONTEXT_TYPE.LIST: {
        // First try direct ID lookup
        const listRef = doc(db, `users/${userId}/shoppingLists`, contextId);
        const listSnap = await getDoc(listRef);
        if (listSnap.exists()) {
          return { exists: true, listId: contextId };
        }
        
        // If not found by ID, try to find by name (contextId might be the list name)
        const listsRef = collection(db, `users/${userId}/shoppingLists`);
        const nameQuery = query(listsRef, where('name', '==', contextId), limit(1));
        const nameSnap = await getDocs(nameQuery);
        if (!nameSnap.empty) {
          const foundList = nameSnap.docs[0];
          return { exists: true, listId: foundList.id, resolvedFromName: true };
        }
        
        return { 
          exists: false, 
          message: 'This shopping list no longer exists. It may have been deleted.'
        };
      }

      default:
        return { exists: false, message: 'Unknown link type.' };
    }
  } catch (error) {
    console.error('Error verifying context link:', error);
    return { exists: false, message: 'Unable to verify link. Please try again.' };
  }
}

/**
 * Get icon component name for context type
 */
export function getContextIcon(contextType) {
  switch (contextType) {
    case CONTEXT_TYPE.BUDGET:
      return 'DollarSign';
    case CONTEXT_TYPE.CATEGORY:
      return 'Folder';
    case CONTEXT_TYPE.TRANSACTION:
      return 'Receipt';
    case CONTEXT_TYPE.LIST:
      return 'ShoppingCart';
    case CONTEXT_TYPE.GOAL:
      return 'Target';
    default:
      return 'Link';
  }
}

/**
 * Get navigation path for context type
 */
export function getContextPath(contextType, contextId) {
  switch (contextType) {
    case CONTEXT_TYPE.BUDGET:
      return `/budget?highlight=${contextId}`;
    case CONTEXT_TYPE.CATEGORY:
      return `/budget?filter=${contextId}`;
    case CONTEXT_TYPE.TRANSACTION:
      return `/premium/transactions?highlight=${contextId}`;
    case CONTEXT_TYPE.LIST:
      return `/shopping-list/${contextId}`;
    case CONTEXT_TYPE.GOAL:
      return `/goals?highlight=${contextId}`;
    default:
      return null;
  }
}

/**
 * Get display color for context type
 */
export function getContextColor(contextType) {
  switch (contextType) {
    case CONTEXT_TYPE.BUDGET:
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        hover: 'hover:bg-emerald-100'
      };
    case CONTEXT_TYPE.CATEGORY:
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        hover: 'hover:bg-blue-100'
      };
    case CONTEXT_TYPE.TRANSACTION:
      return {
        bg: 'bg-pink-50',
        text: 'text-pink-700',
        border: 'border-pink-200',
        hover: 'hover:bg-pink-100'
      };
    case CONTEXT_TYPE.LIST:
      return {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-200',
        hover: 'hover:bg-orange-100'
      };
    case CONTEXT_TYPE.GOAL:
      return {
        bg: 'bg-indigo-50',
        text: 'text-indigo-700',
        border: 'border-indigo-200',
        hover: 'hover:bg-indigo-100'
      };
    default:
      return {
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        border: 'border-purple-200',
        hover: 'hover:bg-purple-100'
      };
  }
}

/**
 * Create context link object
 */
export function createContextLink(type, id, label) {
  if (!type || type === CONTEXT_TYPE.NONE || !id) {
    return {
      contextType: CONTEXT_TYPE.NONE,
      contextId: null,
      contextLabel: label || null
    };
  }

  return {
    contextType: type,
    contextId: id,
    contextLabel: label
  };
}

/**
 * Check if task has a valid context link
 */
export function hasContextLink(task) {
  return task?.contextType && 
         task?.contextType !== CONTEXT_TYPE.NONE && 
         task?.contextId;
}

/**
 * Parse legacy linkedType/linkedRef to new context system
 */
export function migrateToContextLink(task) {
  // Already has new format
  if (task.contextType) {
    return task;
  }

  // Has legacy format
  if (task.linkedType && task.linkedType !== 'none' && task.linkedRef) {
    return {
      ...task,
      contextType: task.linkedType,
      contextId: task.linkedRef,
      contextLabel: task.linkedLabel || task.contextLabel
    };
  }

  // Has only contextLabel (Phase 1)
  if (task.contextLabel && !task.contextType) {
    return {
      ...task,
      contextType: CONTEXT_TYPE.NONE,
      contextId: null
    };
  }

  return task;
}
